import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";

const REASONS = [
  "Item not as described",
  "Wrong size / fit",
  "Damaged on arrival",
  "Wrong item shipped",
  "Quality not as expected",
  "Changed my mind",
  "Other",
];

const METHODS = [
  { value: "upi", label: "UPI (instant)" },
  { value: "bank", label: "Bank transfer (NEFT/IMPS)" },
  { value: "store_credit", label: "Store credit (+5% bonus)" },
  { value: "original", label: "Refund to original payment method" },
];

const Field = ({ label, children, required }) => (
  <div>
    <label className="overline text-neutral-500 block mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const fmtStepDate = (ts) =>
  ts
    ? new Date(ts * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : "";

const ReturnTracker = ({ initialQuery = "", autoRun = false }) => {
  const [query, setQuery] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const lookup = async (q = query) => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/returns/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Please check what you entered."
        );
      }
      setResults(data.results);
    } catch (err) {
      setResults(null);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (autoRun && initialQuery) lookup(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="border border-black/10 p-5 md:p-6 bg-white" data-testid="returns-tracker">
      <p className="overline text-neutral-500 mb-1">Track your return</p>
      <p className="text-sm text-neutral-600 mb-4">
        Enter your Request ID (like RET-1A2B3C), the email, or the mobile number you used on the form.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          lookup();
        }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <input
          className="flex-1 min-w-0 border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
          placeholder="Request ID, email or mobile number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          required
          data-testid="returns-track-query"
        />
        <button type="submit" disabled={busy} className="btn-ink disabled:opacity-50" data-testid="returns-track-submit">
          {busy ? "Checking…" : "Track"}
        </button>
      </form>
      {error && (
        <p className="text-sm text-red-600 mt-3" data-testid="returns-track-error">{error}</p>
      )}
      {results && results.map((track) => {
        const warn = track.status.includes("unreachable") || track.status === "rejected";
        return (
          <div key={track.ref} className="mt-6 pt-5 border-t border-black/5" data-testid={`returns-track-result-${track.ref}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <p className="font-mono text-sm tracking-wider">{track.ref}</p>
              <p className={`text-xs uppercase tracking-[0.18em] px-2 py-1 border ${warn ? "border-rose-300 text-rose-700 bg-rose-50" : "border-emerald-300 text-emerald-800 bg-emerald-50"}`}>
                {track.label}
              </p>
            </div>
            {track.items && <p className="text-xs text-neutral-500 mt-1 line-clamp-1">{track.items}</p>}
            <p className="text-sm text-neutral-600 mt-2" data-testid="returns-track-desc">{track.description}</p>
            <ol className="mt-5">
              {track.steps.map((st, i) => (
                <li key={st.key} className="flex gap-3" data-testid={`returns-step-${st.key}`}>
                  <div className="flex flex-col items-center">
                    {st.failed ? (
                      <Circle className="w-5 h-5 text-rose-600 fill-rose-600" />
                    ) : st.done ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : st.current ? (
                      warn ? <Circle className="w-5 h-5 text-rose-600 fill-rose-600" /> : <Loader2 className="w-5 h-5 text-black" />
                    ) : (
                      <Circle className="w-5 h-5 text-neutral-300" />
                    )}
                    {i < track.steps.length - 1 && (
                      <span className={`w-px flex-1 min-h-[22px] ${st.failed && st.done ? "bg-rose-300" : st.done ? "bg-emerald-600" : "bg-neutral-200"}`} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className={`text-sm ${st.current ? "font-semibold text-black" : st.done ? "text-neutral-800" : "text-neutral-400"}`}>
                      {st.label}
                    </p>
                    {st.at && <p className="text-xs text-neutral-500">{fmtStepDate(st.at)}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
};

const Returns = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    order_no: "",
    items: "",
    reason: "",
    reason_details: "",
    refund_method: "upi",
    refund_details: "",
    request_type: "refund",
    replacement_details: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/returns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail =
          typeof data.detail === "string"
            ? data.detail
            : Array.isArray(data.detail)
            ? data.detail.map((d) => d.msg).join(", ")
            : "Could not submit your request.";
        throw new Error(detail);
      }
      setSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <>
        <Seo title="Return request received" />
        <div className="lux-container py-16 md:py-24 max-w-2xl" data-testid="returns-success">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            <p className="overline text-emerald-700">Request received</p>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl">
            {success.request_type === "replacement"
              ? "Thank you — your replacement request is in."
              : "Thank you — your refund is on its way."}
          </h1>
          <p className="text-neutral-600 mt-6 leading-relaxed">
            {success.message}
          </p>
          <div className="bg-neutral-50 border border-black/5 p-5 mt-8">
            <p className="overline text-neutral-500 mb-1">Reference number</p>
            <p
              className="font-mono text-2xl tracking-wider"
              data-testid="returns-success-ref"
            >
              {success.ref}
            </p>
            <p className="text-xs text-neutral-500 mt-2">
              Save this reference — you can track your return with it on this page any time, and our team will quote it when they reach out on WhatsApp.
            </p>
          </div>
          <div className="mt-8">
            <ReturnTracker initialQuery={success.ref} autoRun />
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/" className="btn-ink" data-testid="returns-success-home">
              Back to home
            </Link>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20I%20just%20submitted%20a%20return%20request%20%E2%80%94%20ref%20%23`}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp"
              data-testid="returns-success-whatsapp"
            >
              Notify us on WhatsApp
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Returns & Refunds"
        description="Submit a return or refund request. Refunds are processed within 7 working days of pickup and QC."
      />
      <div className="lux-container py-12 md:py-16 max-w-2xl" data-testid="returns-page">
        <p className="overline text-neutral-500 mb-3">Customer care</p>
        <h1 className="font-serif-display text-4xl md:text-5xl">
          Returns, Refunds &amp; Replacements
        </h1>
        <p className="text-neutral-600 mt-6 leading-relaxed">
          Not in love? You have <strong>7 days from delivery</strong> to raise
          a return. Once the item reaches us and clears QC, your refund is
          processed within <strong>7 working days</strong> to your chosen
          method.
        </p>
        <ul className="text-sm text-neutral-600 mt-5 space-y-1 list-disc pl-5">
          <li>Pickup is arranged on us — we&apos;ll coordinate on WhatsApp.</li>
          <li>Original tags, dust bag and packaging must be intact.</li>
          <li>Prefer a replacement? Choose &ldquo;Replacement&rdquo; in the form (another size or colour, or a different piece). It is subject to stock &mdash; if it isn&apos;t available we&apos;ll offer a refund or store credit instead.</li>
          <li>Worn, washed, altered or fragranced pieces aren&apos;t eligible.</li>
        </ul>

        <div className="mt-8">
          <ReturnTracker />
        </div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="returns-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Full name" required>
              <input
                required
                value={form.name}
                onChange={set("name")}
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                data-testid="returns-name"
              />
            </Field>
            <Field label="Phone (with country code)" required>
              <input
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 …"
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                data-testid="returns-phone"
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                autoComplete="email"
                data-testid="returns-email"
              />
            </Field>
            <Field label="Order number / WhatsApp ref">
              <input
                value={form.order_no}
                onChange={set("order_no")}
                placeholder="e.g. WA-204 or skip"
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                data-testid="returns-order-no"
              />
            </Field>
          </div>

          <Field label="Item(s) to return" required>
            <textarea
              required
              rows={2}
              value={form.items}
              onChange={set("items")}
              placeholder="e.g. Gucci Marmont mini — black"
              className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent resize-none"
              data-testid="returns-items"
            />
          </Field>

          <Field label="Reason for return" required>
            <select
              required
              value={form.reason}
              onChange={set("reason")}
              className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
              data-testid="returns-reason"
            >
              <option value="">Select a reason…</option>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Anything we should know? (optional)">
            <textarea
              rows={3}
              value={form.reason_details}
              onChange={set("reason_details")}
              placeholder="Photos or extra context help us close this faster."
              className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent resize-none"
              data-testid="returns-reason-details"
            />
          </Field>

          <Field label="What would you like?" required>
            <div className="grid grid-cols-2 gap-3 mt-1">
              {[
                { value: "refund", label: "Refund" },
                { value: "replacement", label: "Replacement" },
              ].map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm({ ...form, request_type: t.value })}
                  className={`text-left border p-3 text-sm transition-colors ${
                    form.request_type === t.value
                      ? "border-black bg-black text-white"
                      : "border-black/15 hover:border-black/40"
                  }`}
                  data-testid={`returns-type-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          {form.request_type === "replacement" && (
            <Field label="Which size / colour / item do you want instead?" required>
              <textarea
                required
                rows={2}
                value={form.replacement_details}
                onChange={set("replacement_details")}
                placeholder="e.g. Same style in size 38, or the black one instead"
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent resize-none"
                data-testid="returns-replacement-details"
              />
            </Field>
          )}

          {form.request_type === "refund" && (
            <>
            <Field label="Preferred refund method" required>
              <div className="grid grid-cols-2 gap-3 mt-1">
                {METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setForm({ ...form, refund_method: m.value })}
                    className={`text-left border p-3 text-sm transition-colors ${
                      form.refund_method === m.value
                        ? "border-black bg-black text-white"
                        : "border-black/15 hover:border-black/40"
                    }`}
                    data-testid={`returns-method-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field
              label={
                form.refund_method === "upi"
                  ? "UPI ID"
                  : form.refund_method === "bank"
                  ? "Bank — account number, IFSC, holder name"
                  : form.refund_method === "store_credit"
                  ? "Confirm — email on which to issue store credit"
                  : "Last 4 digits / reference of original payment"
              }
              required
            >
              <textarea
                required
                rows={2}
                value={form.refund_details}
                onChange={set("refund_details")}
                className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent resize-none"
                data-testid="returns-refund-details"
              />
            </Field>
            </>
          )}

          {error && (
            <p className="text-sm text-red-600" data-testid="returns-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-ink w-full disabled:opacity-50"
            data-testid="returns-submit"
          >
            {busy ? "Submitting…" : "Submit return request"}
          </button>

          <p className="text-xs text-neutral-500 text-center pt-2">
            By submitting you agree to the Luxsova returns policy. Refunds are
            initiated within 7 working days of pickup and QC.
          </p>
        </form>
      </div>
    </>
  );
};

export default Returns;
